/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { FormArray } from '@angular/forms';
import {
  AbstractFormComponent,
  BuilderNavigationService,
  BuilderUserContext,
  CommonActions,
  FormActionFlows,
  FormActions,
  FormFlowState,
  getFormStatePath,
  whenFormInvalid,
} from '@gh/builder-shared';
import { ContactDeliveryMethod, ContactType } from '@gh/builder-shared-data';
import {
  CONTEXTABLE_COMPONENT,
  ContextableComponent,
  ContextRoute,
  createContextableDialogRoute,
  createContextRoute,
  getContextRouteInitData,
  getContextRouteParam,
  NavTabBarComponent,
} from '@gh/common-internal-shared';
import {
  ActionFlow,
  AppRouter,
  compoundLogicalAction,
  EnumDictionary,
  exitAction,
  FormState,
  LogicalAction,
} from '@gh/common-shared';
import { EntityVersion, ID } from '@gh/common-shared-data';

import { isMessageBasket, Message, MessageBasket } from '@gh/core-messages';
import { TranslationService } from '@gh/core-mls';

import {
  DialogService,
  noAnswer,
  NotificationService,
  WaitingService, yesAnswer,
  yesAsDefaultAnswer,
  yesNoConfirmation,
} from '@gh/core-ui';
import { filter$ } from '@gh/rx/operators';
import { assign, filter, find, get, isEmpty, last, map, merge } from 'lodash';
import { getContactName, formatContactPhones } from '../../contact-details-util';
import {
  ContactDetailsCommonTabComponent,
  ContactDetailsCreditorTabComponent,
  ContactDetailsCustomerTabComponent,
  ContactDetailsDebtorTabComponent,
  ContactDetailsEmployeeTabComponent,
  ContactDetailsFinanceCompanyTabComponent,
  ContactDetailsOtherTabComponent,
} from '../../index';
import { ContactDetailsBusinessService } from '../../resources/contact-details-business.service';
import { ContactDetailsType } from '../../resources/model/contact-details-common.model';
import {
  ContactDetails,
  extractRequestedInfo,
  hasContactType,
} from '../../resources/model/contact-details.model';
import { ContactDetailsModel } from '../../services/contact-details-model.service';
import { ContactDetailsCityCountyFormState } from '../contact-details-city-county-tab/contact-details-city-county-form.service';
import { ContactDetailsCityCountyTabComponent } from '../contact-details-city-county-tab/contact-details-city-county-tab.component';
import { ContactDetailsCommonFormState } from '../contact-details-common-tab/contact-details-common-form.service';
import { ContactDetailsCreditorCommonFormState } from '../contact-details-creditor-common-tab/contact-details-creditor-common-form.service';
import { ContactDetailsCreditorSubContractorFormState } from '../contact-details-creditor-sub-contractor-tab/contact-details-creditor-sub-contractor-form.service';
import { ContactDetailsCreditorFormState } from '../contact-details-creditor-tab/contact-details-creditor-form.service';
import { ContactDetailsCustomerAdditionalDetailsFormState } from '../contact-details-customer-additional-details-tab/contact-details-customer-additional-details-form.service';
import { ContactDetailsCustomerCommonFormState } from '../contact-details-customer-common-tab/contact-details-customer-common-form.service';
import { ContactDetailsCustomerFormState } from '../contact-details-customer-tab/contact-details-customer-form.service';
import { ContactDetailsDebtorCommonFormState } from '../contact-details-debtor-common-tab/contact-details-debtor-common-form.service';
import { ContactDetailsDebtorFormState } from '../contact-details-debtor-tab/contact-details-debtor-form.service';
import { ContactDetailsEmployeeCommonFormState } from '../contact-details-employee-common-tab/contact-details-employee-common-form.service';
import { ContactDetailsEmployeeFormDelegator } from '../contact-details-employee-tab/contact-details-employee-form.service';
import { ContactDetailsFinanceCompanyCommonFormState } from '../contact-details-finance-company-common-tab/contact-details-finance-company-common-form.service';
import { ContactDetailsFinanceCompanyFormState } from '../contact-details-finance-company-tab/contact-details-finance-company-form.service';
import { ContactDetailsOtherFormState } from '../contact-details-other-tab/contact-details-other-form.service';
import { ContactDetailsSharedCommunicationListFormStateFactory } from '../contact-details-shared-communication-list-tab/contact-details-shared-communication-list-form.service';
import { ContactDetailsSharedCustomFieldsFormStateFactory } from '../contact-details-shared-custom-fields-tab/contact-details-shared-custom-fields-form.service';
import { ContactDetailsSharedTermsPayeeFormStateFactory } from '../contact-details-shared-terms-tab/contact-details-shared-terms-payee-form.service';
import { ContactDetailsSharedTermsPayerFormStateFactory } from '../contact-details-shared-terms-tab/contact-details-shared-terms-payer-form.service';
import { ContactDetailsFormState } from './contact-details-form.service';

@Component({
  standalone: false,
  selector: 'gh-contact-details-tab-container',
  templateUrl: 'contact-details-tab-container.component.html',
  styleUrls: ['contact-details-tab-container.component.scss'],
  viewProviders: [
    ContactDetailsModel,
    ContactDetailsFormState,
    ContactDetailsCommonFormState,
    ContactDetailsEmployeeFormDelegator,
    ContactDetailsEmployeeCommonFormState,
    ContactDetailsCreditorFormState,
    ContactDetailsCreditorCommonFormState,
    ContactDetailsCreditorSubContractorFormState,
    ContactDetailsCityCountyFormState,
    ContactDetailsCustomerFormState,
    ContactDetailsCustomerCommonFormState,
    ContactDetailsCustomerAdditionalDetailsFormState,
    ContactDetailsFinanceCompanyFormState,
    ContactDetailsFinanceCompanyCommonFormState,
    ContactDetailsDebtorFormState,
    ContactDetailsDebtorCommonFormState,
    ContactDetailsSharedCustomFieldsFormStateFactory,
    ContactDetailsSharedTermsPayerFormStateFactory,
    ContactDetailsSharedTermsPayeeFormStateFactory,
    ContactDetailsSharedCommunicationListFormStateFactory,
    ContactDetailsBusinessService,
    ContactDetailsOtherFormState,
  ],
})
export class ContactDetailsTabContainerComponent extends AbstractFormComponent<ContactDetails> implements OnInit {
  @ViewChild(NavTabBarComponent, { static: true }) navigation: NavTabBarComponent;

  routes = [
    createContextRoute({
      component: ContactDetailsCommonTabComponent,
      path: 'common',
      init: {
        service: ContactDetailsBusinessService,
        selector: 'initCommonTabView',
      },
    }),
    createContextRoute({
      component: ContactDetailsEmployeeTabComponent,
      path: 'employee',
    }),
    createContextRoute({
      component: ContactDetailsCityCountyTabComponent,
      path: 'cityCounty',
    }),
    createContextRoute({
      component: ContactDetailsCreditorTabComponent,
      path: 'creditorSupplier',
    }),
    createContextRoute({
      component: ContactDetailsCustomerTabComponent,
      path: 'customer',
    }),
    createContextRoute({
      component: ContactDetailsFinanceCompanyTabComponent,
      path: 'financeCompany',
    }),
    createContextRoute({
      component: ContactDetailsDebtorTabComponent,
      path: 'debtor',
    }),
    createContextRoute({
      component: ContactDetailsOtherTabComponent,
      path: 'other',
    }),
  ];

  private _title: string = ' ';
  private contactTypes: EnumDictionary<ContactType>;
  private deliveryMethods: EnumDictionary<ContactDeliveryMethod>;

  constructor(public model: ContactDetailsModel,
              public form: ContactDetailsFormState,
              public commonForm: ContactDetailsCommonFormState,
              private route: ContextRoute,
              private contactBusinessService: ContactDetailsBusinessService,
              private userContext: BuilderUserContext,
              private notificationService: NotificationService,
              private dialogService: DialogService,
              private translationService: TranslationService,
              private router: AppRouter,
              private navigationService: BuilderNavigationService,
              private commonActions: CommonActions,
              private formActions: FormActions,
              private flows: FormActionFlows,
              private waitings: WaitingService,
              @Inject(CONTEXTABLE_COMPONENT) private contextable: ContextableComponent) {
    super();
  }

  get title(): string {
    return this._title;
  }

  get parentRoute(): string {
    return this.model.parentRoute;
  }

  get parentTitle(): string {
    return this.model.parentTitle || '';
  }

  get supTitle(): string {
    const original = this.model.original;

    if (!original) {
      return '';
    }

    const { id } = original;
    return id ? `${this.translationService.get('lbl_id_lc')} ${id}` : '';
  }

  get currentTab(): string {
    return this.model.masterTab;
  }

  public trimCountryCode(phoneNumber: string, countryCode: string): string {
    return phoneNumber.startsWith(countryCode) ? phoneNumber.slice(countryCode.length) : phoneNumber;
  }

  get processFlow(): ActionFlow<FormFlowState<ContactDetails>> {
    const { creationMode } = this.model;

    return this.flows.updateFlow(this.saveAction, (state) => {
      const details = state.value;

      if (this.model.contactType) {
        this.contextable.apply(this.buildRequestedInfo(details.id!, details.version!, this.form.submitValue));
      } else if (creationMode) {
        this.router.switchTo(['/contacts/', details.id]);
      } else {       
        formatContactPhones(details);        
        this.form.group.markAsPristine();
        this.model.setOriginal(details);
        this.form.build(details);
      }
    });
  }

  get deactivateFlow(): ActionFlow<FormFlowState<ContactDetails>> {
    return this.flows.deactivateFlow(this.saveAction);
  }

  get saveAction(): LogicalAction<FormFlowState<ContactDetails>> {
    return compoundLogicalAction('save.success', 'save.fail', {
      'start': this.commonActions.validateWithEffect(whenFormInvalid, () => this.showError())
        .bindAs('validate', 'validateForm.fail'),
      'validate': this.commonActions.validate<FormFlowState<ContactDetails>>((state) => this.validate(state))
        .bindAs('execute', 'validate.fail'),
      'execute': this.commonActions.execute((state: FormFlowState<ContactDetails>) =>
          this.model.creationMode
            ? this.contactBusinessService.create(state.value)
            : this.contactBusinessService.update(state.value))
        .bindAs('execute.success', 'validate.fail'),
      'execute.success': this.formActions.successNotification().bindAs('success'),
      'validate.fail': (dispatch, state: FormFlowState<ContactDetails>) => {
        if (isMessageBasket(state.error)
          && state.error.has('msg_contact_email_show_warning') && state.error.count() === 1) {
          const confirmation$ = this.dialogService.confirmation(yesNoConfirmation('msg_contact_email_show_warning'));

          confirmation$.pipe(
            filter$(noAnswer))
            .subscribe(() => dispatch('execute', {
              ...state,
              value: merge({}, state.value, <any>{
                common: { confirmations: { skipEmailRequirementCheck: true } },
              }),
            }));

          confirmation$.pipe(
            filter$(yesAsDefaultAnswer))
            .subscribe(() => dispatch('fail', state));
        } else {
          dispatch('handleErrors', state);
        }
      },
      'handleErrors': (dispatch, state: FormFlowState<ContactDetails>) => {
        const errors = state.error;

        if (isMessageBasket(errors) && errors.has('msg_check_duplicates')) {
          this.navigation.navigateTo('common');
        }

        if (isMessageBasket(errors) && errors.has('msg_field_required')) {
          const msg = <Message>errors.get('msg_field_required');

          if (msg.params['field'] === 'email') {
            const commonForm = <ContactDetailsCommonFormState>this.form.getChildForm('common');

            commonForm.setEmailRequired();

            this.navigation.navigateTo('common');
          }

        }

        this.notificationService.processAll(errors);
      },
      'success': exitAction<FormFlowState<ContactDetails>>('save.success'),
      '*': exitAction<FormFlowState<ContactDetails>>('save.fail'),
    });
  }

  get isVisibleSyncBtn(): boolean {
    return this.model.editMode && this.isCustomerOrSupplier();
  }

  setTitle(title: string): void {
    this._title = title;
  }

  ngOnInit(): void {
    assign(this, getContextRouteInitData(this.route));

    const data = this.model.original;

    this.form.build(data);

    this.setTitle(getContactName(this.commonForm.value));

    if (this.model.contactId && this.userContext.officeView) {
      this.contactBusinessService.checkSyncStatus(this.model.contactId).subscribe();
    }
  }

  onTabChanged(name: string): void {
    if (this.model.masterTab !== name) {
      this.model.goToTab(name);
    }
  }

  isTabAvailable(type: ContactType): boolean {
    const contactTypesArray = <FormArray>this.form.group.get('common.contactTypes');

    if (contactTypesArray) {
      return !!find(
        contactTypesArray.controls,
        ({ value }: { value: ContactDetailsType }) => value.type === type && value.visible && value.assigned);
    } else {
      return false;

    }
  }

  isCustomerOrSupplier(): boolean {
    return this.isTabAvailable('CreditorSupplier') || this.isTabAvailable('Customer');
  }

  assignType(type: ContactType): void {
    const tab = this.form.findTabForm(type);

    this.form.assignTabForm(tab, this.model.original);

    this.commonForm.assignType(type);

  }

  unAssignType(type: ContactType): void {
    const tab = this.form.findTabForm(type);
    this.form.unAssignTabForm(tab);
    this.commonForm.unAssignType(type);
  }

  goToAel(): void {
    this.navigationService.goToAel({
      label: 'lbl_edit_contact',
      entityType: 'CONTACT',
      entityId: this.model.contactId,
    });
  }

  syncWithXero(): void {
    const confirmation$ = this.dialogService.confirmation(yesNoConfirmation('msg_contact_sync_xero_show_warning'));

    confirmation$.pipe(
      filter$(yesAnswer))
      .subscribe(() => {
        this.contactBusinessService.syncWithXero(this.model.contactId).pipe(
          this.waitings.operator())
          .pipe(this.waitings.operator())
          .subscribe(
            () => this.notificationService.success('msg_contact_sync_with_xero_task'),
            this.notificationService.processAll,
          );
      });
  }

  private buildRequestedInfo(contactId: ID, version: EntityVersion, contact: ContactDetails): object {
    const entryPoint = getContextRouteParam(this.route, 'entryPoint');
    return extractRequestedInfo(contactId, version, contact, entryPoint);
  }

  private showError(): void {
    const invalidFormPath = this.form.findNestedForm((form) => form.group.invalid);

    if (!isEmpty(invalidFormPath)) {
      const formPathArray = getFormStatePath(invalidFormPath);
      const invalidForm = last(invalidFormPath)!.form;
      
      this.model.goToTab(formPathArray);
      
      setTimeout(() => {
        invalidForm.markAllAsTouched();
      }, 0);
    }
  }

  private validate(state: FormFlowState<ContactDetails>): Maybe<MessageBasket> {
    const { value: details, form } = state;
    const contactTypes = details.common.contactTypes;

    // main validations that stops validation process
    if (contactTypes) {
      const assigned = contactTypes.filter((type) => type.assigned);

      if (assigned.length === 0) {
        return MessageBasket.error('msg_contact_type_required');
      }

      if (Array.isArray(this.model.contactType)) {
        const notAssigned = filter(this.model.contactType, (contactType: any) =>
          !find(contactTypes, { type: contactType, assigned: true }));

        if (notAssigned && notAssigned.length === this.model.contactType.length) {
          return MessageBasket.error('msg_contact_type_required_not_assigned', {
            contactType: map(this.model.contactType, (type: ContactType) =>
              this.contactTypes.get(type)).join(` ${this.translationService.get('lbl_or')} `),
          });
        }
      } else if (this.model.contactType && !find(contactTypes, { type: this.model.contactType, assigned: true })) {
        return MessageBasket.error('msg_contact_type_required_not_assigned', {
          contactType: this.contactTypes.get(this.model.contactType),
        });
      }
    }

    // collectable validations
    const errors: Message[] = [];

    if ((<FormState<ContactDetails>>form).getChildForm('common').confirmations.hasError('duplicates')) {
      errors.push(Message.error('msg_check_duplicates'));
    }

    if (hasContactType(details, ContactType.Customer) && details.customer) {
      errors.push(...this.validateBillingInfo(details, details.customer!.common.billing));
    }

    if (hasContactType(details, ContactType.Debtor) && details.debtor) {
      errors.push(...this.validateBillingInfo(details, details.debtor!.common.billing));
    }

    if (errors.length > 0) {
      return MessageBasket.create(errors);
    }

    // final validations that should be called only when all other validations were successed
    if (!get(details.common.confirmations, 'skipEmailRequirementCheck', true)) {
      return MessageBasket.error('msg_contact_email_show_warning');
    }
  }

  private validateBillingInfo(details: ContactDetails,
                              { deliveryBy, isBillingSame }: { deliveryBy: ContactDeliveryMethod; isBillingSame: boolean }): Message[] {
    if (deliveryBy === ContactDeliveryMethod.Email && isBillingSame && isEmpty(details.common.email)) {
      return [Message.error('msg_contact_email_common_required', {
        deliveryMethod: this.deliveryMethods.get(deliveryBy),
      })];
    } else {
      return [];
    }
  }
}

export const CONTACT_DETAILS_CONTEXT_ROUTE = createContextRoute({
  path: 'details',
  component: ContactDetailsTabContainerComponent,
  init: ContactDetailsBusinessService,
  leave: true,
});

export const contactDetailsDialogRoute = createContextableDialogRoute(CONTACT_DETAILS_CONTEXT_ROUTE);
